import { useCallback, useEffect, useRef, useState } from 'react';

import {
  type ClusterClaimResponseOutput,
  useClaimCluster,
} from '@/api/generated';
import { getToken } from '@/routes/(auth)/lib/token';

// The server keeps a claim 5 minutes past its last renewal.
const RENEW_MS = 60_000;
// Stop renewing after this long without the reviewer touching the page, so a
// forgotten tab frees its cluster.
const IDLE_MS = 15 * 60_000;
// Releases wait a moment: React's dev double mount, or leaving a cluster and
// coming straight back, must not drop a claim the page still uses.
const RELEASE_DELAY_MS = 1000;

const pendingReleases = new Map<string, ReturnType<typeof setTimeout>>();

// fetch with keepalive, so the request still goes out while the tab closes.
const releaseNow = (clusterId: string) => {
  fetch(`${import.meta.env.VITE_BACKEND_URL}/api/clusters/${clusterId}/claim`, {
    method: 'DELETE',
    keepalive: true,
    headers: { Authorization: `Bearer ${getToken()}` },
  }).catch(() => undefined);
};

/**
 * Keeps the cluster locked to this reviewer while the page is open, visible
 * and in use, and releases it when they leave. `claim` is undefined until the
 * first answer; `mine: false` means someone else has the cluster open.
 */
export const useClusterClaim = (clusterId: string) => {
  const [claim, setClaim] = useState<ClusterClaimResponseOutput>();
  const { mutateAsync } = useClaimCluster();
  const lastActive = useRef(Date.now());
  const idle = useRef(false);

  const renew = useCallback(async () => {
    try {
      setClaim(await mutateAsync({ clusterId: Number(clusterId) }));
    } catch {
      // The next renewal tries again; the save checks the lock anyway.
    }
  }, [clusterId, mutateAsync]);

  useEffect(() => {
    const pending = pendingReleases.get(clusterId);
    if (pending) {
      clearTimeout(pending);
      pendingReleases.delete(clusterId);
    }
    renew().catch(() => undefined);

    const timer = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastActive.current > IDLE_MS) {
        idle.current = true;
        return;
      }
      renew().catch(() => undefined);
    }, RENEW_MS);

    // Back from idle, from another tab or from sleep: the claim may have
    // lapsed, so ask again straight away.
    const onActive = () => {
      lastActive.current = Date.now();
      if (!idle.current) return;
      idle.current = false;
      renew().catch(() => undefined);
    };
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      idle.current = true;
      onActive();
    };
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) onVisible();
    };
    const onPageHide = () => {
      releaseNow(clusterId);
    };
    const activity = ['pointerdown', 'pointermove', 'keydown', 'wheel'];
    for (const name of activity) {
      window.addEventListener(name, onActive, { passive: true });
    }
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('pagehide', onPageHide);

    return () => {
      clearInterval(timer);
      for (const name of activity) {
        window.removeEventListener(name, onActive);
      }
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('pagehide', onPageHide);
      pendingReleases.set(
        clusterId,
        setTimeout(() => {
          pendingReleases.delete(clusterId);
          releaseNow(clusterId);
        }, RELEASE_DELAY_MS)
      );
    };
  }, [clusterId, renew]);

  return { claim, setClaim };
};
